import { useToastManager } from "@porsche-design-system/components-react"
import type { ListingDescriptionEditView, ListingDescriptionView } from "@slfinrtl/ui-bff-routes"
import { getListingDescriptionEditViewRoute, getListingDescriptionViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { AnimatedStage, listingDescriptionMessages } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useConfig } from "src/hooks"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useVehicleDetailsContextForStage } from "../../context/VehicleDetailsContext"
import { useEditActionButton } from "../../hooks/useEditActionButton"
import { ListingDescription } from "./components/ListingDescription"
import { ListingDescriptionFormContainer } from "./listingDescriptionFormContainer/ListingDescriptionFormContainer"
import { listingDescriptionStageId } from "./listingDescriptionStageId"

export const ListingDescriptionContainer = withErrorHandler(() => {
	const { formatMessage } = useIntl()
	const [view, setView] = useState<ListingDescriptionView>()
	const { inventoryId, mode, setEditStage } = useVehicleDetailsContextForStage("listingDescription")
	const { addMessage } = useToastManager()
	const { aiListingDescriptionEnabled } = useConfig()

	const fetchView = useBff(
		getListingDescriptionViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const ref = useRef(null)

	useEffect(() => {
		fetchView().then(setView)
	}, [fetchView])

	const fetchEditView = useBff(
		getListingDescriptionEditViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const [editView, setEditView] = useState<ListingDescriptionEditView | undefined>()
	const [isEditViewLoading, setIsEditViewLoading] = useState(false)

	const handleEditClick = useCallback((): Promise<void> => {
		setIsEditViewLoading(true)
		return fetchEditView()
			.then((result) => {
				setEditView(result)
				setEditStage("listingDescription")
			})
			.finally(() => setIsEditViewLoading(false))
	}, [fetchEditView, setEditStage])

	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "listingDescription",
		ref,
	})

	const onExit = useCallback(() => {
		setEditStage(undefined)
	}, [setEditStage])

	const onSaved = useCallback(
		(v: ListingDescriptionView) => {
			setView(v)
			onExit()
			addMessage({
				state: "success",
				text: formatMessage(commonMessages.saveSuccess),
			})
		},
		[addMessage, formatMessage, onExit],
	)

	const actionButton = useEditActionButton("listingDescription", view?.editAction, {
		fetch: handleEditClick,
		isLoading: isEditViewLoading,
	})

	return (
		<AnimatedStage
			ref={ref}
			id={listingDescriptionStageId}
			title={formatMessage(listingDescriptionMessages.title)}
			isNew={aiListingDescriptionEnabled}
			actionButtons={actionButton}
		>
			<ErrorNotificationInline dismissButton={false} />
			{mode !== "edit" || !editView ? (
				view && <ListingDescription view={view} />
			) : (
				<ListingDescriptionFormContainer
					inventoryId={inventoryId}
					editView={editView}
					onCancel={onExit}
					onSaved={onSaved}
				/>
			)}
		</AnimatedStage>
	)
})
